'use client';

import { useCallback, useEffect, useRef } from 'react';
import { noop } from 'utils/noop';
import {
  MODAL_ENTER_DURATION,
  MODAL_EXIT_DURATION,
} from './animation-constants';

/**
 * Hook for managing timed callbacks for component transitions (entering and exiting).
 * This is particularly useful for handling animation callbacks in modals, dialogs, etc.
 *
 * Key features:
 * - Handles both enter and exit animations with proper timing
 * - Supports interruptions (e.g., if entering is interrupted by exiting)
 * - Only calls callbacks when the transition fully completes
 * - Automatically cleans up timeouts when component unmounts
 *
 * @param {Object} options Configuration options for the hook
 * @param {boolean} options.isShowing Whether the component is currently showing
 * @param {number} options.enterDuration Duration in ms for the enter animation
 * @param {number} options.exitDuration Duration in ms for the exit animation
 * @param {() => void} options.onEntered Callback fired when enter animation completes
 * @param {() => void} options.onExited Callback fired when exit animation completes
 * @param {boolean} options.disableAnimation Whether to disable the animation
 */
export function useTimeoutCallbacks({
  isShowing,
  enterDuration = MODAL_ENTER_DURATION,
  exitDuration = MODAL_EXIT_DURATION,
  onEntered = noop,
  onExited = noop,
  disableAnimation = false,
}: {
  isShowing: boolean;
  enterDuration?: number;
  exitDuration?: number;
  onEntered?: () => void;
  onExited?: () => void;
  disableAnimation?: boolean;
}) {
  // Refs to track internal animation states - not exposed
  const isEnteringRef = useRef(false);
  const isExitingRef = useRef(false);

  // Refs to track timers
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track the previous state to detect transitions
  const prevIsShowingRef = useRef(isShowing);

  // Clear all timeouts to avoid memory leaks
  const clearTimeouts = useCallback(() => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }

    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
  }, []);

  // Start enter animation
  const startEnterAnimation = useCallback(() => {
    clearTimeouts();
    isEnteringRef.current = true;
    isExitingRef.current = false;

    enterTimeoutRef.current = setTimeout(() => {
      isEnteringRef.current = false;
      onEntered();
    }, enterDuration);
  }, [clearTimeouts, enterDuration, onEntered]);

  // Start exit animation
  const startExitAnimation = useCallback(() => {
    clearTimeouts();
    isExitingRef.current = true;
    isEnteringRef.current = false;

    exitTimeoutRef.current = setTimeout(() => {
      isExitingRef.current = false;
      onExited();
    }, exitDuration);
  }, [clearTimeouts, exitDuration, onExited]);

  // Handle transitions between states
  useEffect(() => {
    const prevIsShowing = prevIsShowingRef.current;
    prevIsShowingRef.current = isShowing;

    if (disableAnimation) {
      return;
    }

    // Skip on first render if isShowing is false
    if (!prevIsShowing && !isShowing) {
      return;
    }

    // Transition from not showing to showing (enter)
    if (!prevIsShowing && isShowing) {
      startEnterAnimation();
    }

    // Transition from showing to not showing (exit)
    if (prevIsShowing && !isShowing) {
      startExitAnimation();
    }
  }, [isShowing, startEnterAnimation, startExitAnimation, disableAnimation]);

  // Clean up on unmount
  useEffect(() => {
    return clearTimeouts;
  }, [clearTimeouts]);
}
